import type { FilterSchema } from '@/shared/components/filter/validation'
import type {
	OptimizationIDModel,
	ProductsByEngineModel,
	StatisticsInterpretabilityGroupedResponseModel,
	StatisticsInterpretabilityResponse,
} from '@/shared/generated'

import { any, getOr, map, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useProductCountByEngineStatisticsQuery } from '@/entities/bi'
import {
	getFinishedAndLockedOptimizations,
	useOptimizationsQuery,
} from '@/entities/optimization'
import { useInterpretabilityStatisticQuery } from '@/entities/optimization-statistic'

import { Operators } from '@/shared/components/query-builder'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import PercentDifferenceBadge from '../../PercentDifferenceBadge'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetErrorState from '../../WidgetErrorState'

export default function NumericGrossMarginImpactWidget({
	commonFilters,
	className,
}: {
	className?: string
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const optimizationsQuery = useOptimizationsQuery({
		select: getFinishedAndLockedOptimizations,
	})
	const productCountQuery = useProductCountByEngineStatisticsQuery(
		{
			filters: [
				...commonFilters,
				{
					name: 'optimization_group_id',
					operation: Operators.IN,
					value: map(
						prop('optimization_group_id'),
						optimizationsQuery.data ?? []
					),
				},
			],
		},
		{
			enabled: !!optimizationsQuery.data,
			select: computeProductsCount,
		}
	)
	const grossMarginQuery = useInterpretabilityStatisticQuery(
		{
			optimizations: optimizationsQuery.data as OptimizationIDModel[],
			filters: commonFilters,
		},
		{
			enabled: !!optimizationsQuery.data,
			select: calculateGrossMarginStatistics,
		}
	)
	const isLoading = any(prop('isLoading'), [
		grossMarginQuery,
		productCountQuery,
		optimizationsQuery,
	])
	const isError = any(prop('isError'), [
		grossMarginQuery,
		productCountQuery,
		optimizationsQuery,
	])

	return (
		<Widget className={cn('relative truncate', className)}>
			<WidgetHeader
				title={t('widget.gross_margin_impact.title', {
					defaultValue: 'GM Impact',
				})}
				tooltip={t('widget.gross_margin_impact.tooltip', {
					defaultValue:
						'Projected gross margin over the next 7 days based on recommended prices. The % delta shows the uplift versus keeping current prices unchanged.',
				})}
				subtitle={t('widget.gross_margin_impact.subtitle', {
					defaultValue: 'Projected gross margin uplift',
				})}
			/>
			<WidgetErrorState isTinted={isError} size='small' />
			<WidgetContent className='truncate'>
				<div className='mt-auto w-full'>
					{isLoading && <Loader />}
					{!isLoading && (
						<div className='space-y-1'>
							<PercentDifferenceBadge
								value={grossMarginQuery?.data?.grossMarginDeltaPercent ?? 0}
							/>
							<p className='truncate text-3xl font-semibold'>
								{formatNumber(
									grossMarginQuery?.data?.grossMargin ?? 0,
									NumberFormats.Percent
								)}
							</p>
						</div>
					)}
				</div>
			</WidgetContent>
		</Widget>
	)
}
const Loader = () => (
	<div className='space-y-1'>
		<Skeleton className='col-span-2 h-4 w-32' />
		<Skeleton className='col-span-2 h-4 w-32' />
		<Skeleton className='col-span-2 h-8 w-32' />
	</div>
)

const calculateGrossMarginStatistics = (
	data:
		| StatisticsInterpretabilityResponse
		| StatisticsInterpretabilityGroupedResponseModel[]
		| null
) => {
	if (!data) return { grossMargin: 0, grossMarginDeltaPercent: 0 }

	const finalGrossMargin = getOr(
		0,
		['metrics', 'predict_gross_profit_margin.final'],
		data
	)
	const initialGrossMargin = getOr(
		0,
		['metrics', 'predict_gross_profit_margin.init'],
		data
	)
	const grossMarginDeltaPercent =
		((finalGrossMargin - initialGrossMargin) / initialGrossMargin) * 100

	return {
		grossMargin: finalGrossMargin * 100,
		grossMarginDeltaPercent,
	}
}
const computeProductsCount = (data: ProductsByEngineModel[] | null) =>
	data
		?.filter(
			({ pricing_campaign_engine }) => pricing_campaign_engine !== 'locked'
		)
		.map(({ product_count }) => product_count ?? 0)
		.reduce((acc, curr) => acc + curr, 0)
